import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { FuseConfig } from '@fuse/types/fuse-config';
import { AuthService } from 'app/core/services/auth.service';
import { WorkingLaboratoryService } from 'app/core/services/prothesis-api/working-laboratory.service';
import { WorkingCustomerService } from 'app/core/services/prothesis-customer-api/working-customer.service';
import { Area } from 'app/shared/enums/area.enum';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AreaSelectorFormGroup } from './area-selector-form-group';
import { MatRadioGroup, MatRadioButton } from '@angular/material/radio';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'app-area-selector',
  styleUrls: ['./area-selector.component.scss'],
  templateUrl: './area-selector.component.html',
  imports: [FormsModule, ReactiveFormsModule, MatRadioGroup, MatRadioButton, TranslatePipe],
})
export class AreaSelectorComponent implements OnDestroy, OnInit {
  public customerArea = Area.Customer;
  public formGroup: FormGroup<AreaSelectorFormGroup>;
  public fuseConfig: FuseConfig;
  public laboratoryArea = Area.Laboratory;

  private _authService = inject(AuthService);
  private _changeDetectorRef = inject(ChangeDetectorRef);
  private _router = inject(Router);
  private _unsubscribeAll$: Subject<void> = new Subject();
  private _workingCustomerService = inject(WorkingCustomerService);
  private _workingLaboratoryService = inject(WorkingLaboratoryService);

  public ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll$.next();
    this._unsubscribeAll$.complete();
  }

  public ngOnInit(): void {
    this.formGroup = new FormGroup<AreaSelectorFormGroup>({
      area: new FormControl<Area>(null!),
    });

    this._authService.areaConfiguredFlag$.pipe(takeUntil(this._unsubscribeAll$)).subscribe(() => {
      // Set the config form values without emitting an event so that we don't end up with an infinite loop
      this.formGroup.setValue({ area: this._authService.area }, { emitEvent: false });
      this._changeDetectorRef.markForCheck();
    });

    // Subscribe to the form value changes
    this.formGroup.valueChanges.pipe(takeUntil(this._unsubscribeAll$)).subscribe((config) => {
      this._configureArea(config.area);
    });
  }

  private _configureArea(area: Area | null | undefined): void {
    if (area === Area.Laboratory) {
      this._workingCustomerService.clear();
      this._workingLaboratoryService.clear();
      this._authService.configureLaboratoryArea().then(() => {
        this._router.navigateByUrl('');
      });
    } else if (area === Area.Customer) {
      this._workingCustomerService.clear();
      this._workingLaboratoryService.clear();
      this._authService.configureCustomerArea().then(() => {
        this._router.navigateByUrl('');
      });
    } else {
      console.error('Unknown area');
    }
  }
}
